import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { aiImageEditPageLeaveGuard } from 'ai-features/guards/ai-image-edit-page-leave.guard';
import { DalRedirectComponent } from 'authentication/components/dal-redirect/dal-redirect.component';
import { ExternalRedirectComponent } from 'authentication/components/external-redirect/external-redirect.component';
import { UserExternalRedirectComponent } from 'authentication/components/user-external-redirect/user-external-redirect.component';
import { UserRedirectComponent } from 'authentication/components/user-redirect/user-redirect.component';
import { AnonymousGuard } from 'authentication/guards/anonymous.guard';
import { AuthGuard } from 'authentication/guards/auth.guard';
import { SsoPropertyKey, ssoGuard } from 'authentication/guards/sso-iceportal.guard';
import { ExportsRoutingConfig } from 'exports/exports-routing.config';
import { ProfileComponent } from 'shared/components';
import { ResourcePermissionsGuard } from 'shared/guards/resource-permissions.guard';
import { UserOnboardingComponent } from 'users/components';
import { UserNotificationsTableComponent } from 'users/components/user-notifications-table/user-notifications-table.component';

const routes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard',
    },
    {
        path: '',
        loadChildren: () => import('./authentication/authentication.module').then((m) => m.AuthenticationModule),
        canActivate: [AnonymousGuard],
        data: {
            layout: {
                sidebar: false,
                header: false,
            },
        },
    },
    {
        path: 'dashboard',
        loadChildren: () => import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
        canActivate: [AuthGuard],
    },
    {
        path: 'listings',
        loadChildren: () => import('./listings/listings.module').then((m) => m.ListingsModule),
        canActivate: [AuthGuard, ResourcePermissionsGuard],
    },
    {
        path: 'users',
        loadChildren: () => import('./users/users.module').then((m) => m.UsersModule),
        canActivate: [AuthGuard, ResourcePermissionsGuard],
    },
    {
        path: 'settings',
        loadChildren: () => import('./settings/settings.module').then((m) => m.SettingsModule),
        canActivate: [AuthGuard, ResourcePermissionsGuard],
    },
    {
        path: 'exports',
        loadChildren: () => import('./settings/settings.module').then((m) => m.SettingsModule),
        canActivate: [AuthGuard, ResourcePermissionsGuard],
    },
    {
        path: 'my-profile',
        component: ProfileComponent,
        canActivate: [AuthGuard],
        data: {
            pageTitle: 'myUserProfile.label',
            header: {
                breadcrumbs: [{ label: 'myUserProfile.label' }],
            },
        },
    },
    {
        path: 'notifications',
        component: UserNotificationsTableComponent,
        canActivate: [AuthGuard],
        data: {
            pageTitle: 'userNotificationsTable.label',
            header: {
                breadcrumbs: [{ label: 'userNotificationsTable.label' }],
            },
        },
    },
    {
        path: 'register-user/:inviteId',
        component: UserOnboardingComponent,
        canActivate: [AnonymousGuard],
        data: {
            pageTitle: 'generalKeywords.register',
            layout: {
                sidebar: false,
                header: false,
            },
            header: {
                disableUserMenu: true,
                disableNotificationsMenu: true,
            },
        },
    },
    {
        path: 'user-redirect',
        component: UserRedirectComponent,
        canActivate: [AnonymousGuard],
        data: {
            pageTitle: 'generalKeywords.signin',
            layout: {
                sidebar: false,
                header: false,
            },
            header: {
                disableUserMenu: true,
                disableNotificationsMenu: true,
            },
        },
    },
    {
        path: 'external-redirect/auth',
        component: UserExternalRedirectComponent,
        data: {
            pageTitle: 'generalKeywords.userExternalRedirect',
            layout: {
                sidebar: false,
                header: false,
            },
        },
    },
    {
        path: 'ai-image-edit/:listingId/:publicMediaId',
        loadComponent: () =>
            import('./ai-features/components/ai-image-edit-page/ai-image-edit-page.component').then((m) => m.AiImageEditPageComponent),
        canActivate: [AuthGuard],
        canDeactivate: [aiImageEditPageLeaveGuard],
        data: {
            pageTitle: 'aiFeatures.imageEditing.title',
            layout: {
                sidebar: false,
                header: false,
            },
        },
    },
    {
        path: 'icePortal',
        component: ExternalRedirectComponent,
        canActivate: [AuthGuard, ssoGuard(SsoPropertyKey.ICE_PORTAL, 'dashboard')],
    },
    {
        path: 'dal',
        component: DalRedirectComponent,
        canActivate: [AuthGuard, ssoGuard(SsoPropertyKey.DAL, 'dashboard')],
    },
];

const noRouteMatchFallback = {
    path: '**',
    redirectTo: 'dashboard',
};

@NgModule({
    // @ts-ignore
    imports: [RouterModule.forRoot([...routes, ...ExportsRoutingConfig, noRouteMatchFallback])],
    exports: [RouterModule],
})
export class AppRoutingModule {}
